import { apiRequest } from './api';
import { ApiResponse, Category, CategoryFormData, CategoryFilters } from '../types';

class CategoryService {
  // Listar todas as categorias
  async getCategories(filters: CategoryFilters = {}): Promise<ApiResponse<Category[]>> {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    return await apiRequest.get<Category[]>(`/categories?${params.toString()}`);
  }

  // Obter categorias ativas para seleção
  async getActiveCategories(): Promise<ApiResponse<Category[]>> {
    return await apiRequest.get<Category[]>('/categories/active');
  }

  // Obter uma categoria específica
  async getCategory(id: number): Promise<ApiResponse<Category>> {
    return await apiRequest.get<Category>(`/categories/${id}`);
  }

  // Criar nova categoria
  async createCategory(data: CategoryFormData): Promise<ApiResponse<Category>> {
    return await apiRequest.post<Category>('/categories', data);
  }

  // Atualizar categoria
  async updateCategory(id: number, data: Partial<CategoryFormData>): Promise<ApiResponse<Category>> {
    return await apiRequest.put<Category>(`/categories/${id}`, data);
  }

  // Alternar status ativo/inativo
  async toggleStatus(id: number): Promise<ApiResponse<Category>> {
    return await apiRequest.post<Category>(`/categories/${id}/toggle-status`);
  }

  // Deletar categoria
  async deleteCategory(id: number): Promise<ApiResponse> {
    return await apiRequest.delete(`/categories/${id}`);
  }

  // Métodos auxiliares para formatação
  formatColor(cor?: string): string {
    return cor || '#1976d2';
  }

  getIconName(icone?: string): string {
    return icone || 'Category';
  }

  generateCodeFromName(nome: string): string {
    return nome
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove acentos
      .replace(/[^a-z0-9]/g, '_') // Substitui caracteres especiais por underscore
      .replace(/_+/g, '_') // Remove underscores duplicados
      .replace(/^_|_$/g, ''); // Remove underscores do início e fim
  }

  validateCategoryData(data: CategoryFormData): string[] {
    const errors: string[] = [];

    if (!data.nome || data.nome.trim().length < 2) {
      errors.push('Nome deve ter pelo menos 2 caracteres');
    }

    if (!data.codigo || data.codigo.trim().length < 2) {
      errors.push('Código deve ter pelo menos 2 caracteres');
    }

    if (data.codigo && !/^[a-z0-9_]+$/.test(data.codigo)) {
      errors.push('Código deve conter apenas letras minúsculas, números e underscore');
    }

    if (data.cor && !/^#[0-9A-F]{6}$/i.test(data.cor)) {
      errors.push('Cor deve estar no formato hexadecimal (#RRGGBB)');
    }

    return errors;
  }

  // Cores padrão sugeridas
  getDefaultColors(): string[] {
    return [
      '#FF5722', // Deep Orange
      '#4CAF50', // Green
      '#F44336', // Red
      '#FF9800', // Orange
      '#2196F3', // Blue
      '#9C27B0', // Purple
      '#607D8B', // Blue Grey
      '#795548', // Brown
      '#E91E63', // Pink
      '#00BCD4', // Cyan
      '#8BC34A', // Light Green
      '#FFC107', // Amber
      '#673AB7', // Deep Purple
      '#009688', // Teal
      '#CDDC39', // Lime
      '#9E9E9E'  // Grey
    ];
  }

  // Ícones padrão sugeridos
  getDefaultIcons(): Array<{ name: string; label: string }> {
    return [
      { name: 'Home', label: 'Casa' },
      { name: 'Group', label: 'Pessoas' },
      { name: 'AccountBalance', label: 'Banco' },
      { name: 'DirectionsCar', label: 'Carro' },
      { name: 'Business', label: 'Empresa' },
      { name: 'Receipt', label: 'Recibo' },
      { name: 'Payment', label: 'Pagamento' },
      { name: 'ShoppingCart', label: 'Compras' },
      { name: 'Build', label: 'Ferramenta' },
      { name: 'Computer', label: 'Computador' },
      { name: 'Phone', label: 'Telefone' },
      { name: 'Email', label: 'Email' },
      { name: 'Security', label: 'Segurança' },
      { name: 'School', label: 'Educação' },
      { name: 'Restaurant', label: 'Alimentação' },
      { name: 'LocalGasStation', label: 'Combustível' },
      { name: 'ElectricalServices', label: 'Elétrica' },
      { name: 'Plumbing', label: 'Hidráulica' },
      { name: 'CleaningServices', label: 'Limpeza' },
      { name: 'Category', label: 'Categoria' }
    ];
  }
}

const categoryService = new CategoryService();

const categoryServiceMethods = {
  getCategories: categoryService.getCategories.bind(categoryService),
  getCategory: categoryService.getCategory.bind(categoryService),
  createCategory: categoryService.createCategory.bind(categoryService),
  updateCategory: categoryService.updateCategory.bind(categoryService),
  deleteCategory: categoryService.deleteCategory.bind(categoryService),
  toggleStatus: categoryService.toggleStatus.bind(categoryService),
  getActiveCategories: categoryService.getActiveCategories.bind(categoryService),
  validateCategoryData: categoryService.validateCategoryData.bind(categoryService),
  formatColor: categoryService.formatColor.bind(categoryService),
  getIconName: categoryService.getIconName.bind(categoryService),
  generateCodeFromName: categoryService.generateCodeFromName.bind(categoryService),
  getDefaultColors: categoryService.getDefaultColors.bind(categoryService),
  getDefaultIcons: categoryService.getDefaultIcons.bind(categoryService)
};

export default categoryServiceMethods; 